import { debounce } from 'lodash';
import { useState, useCallback, useMemo } from 'react';

import { Search } from '@mui/icons-material';
import { Box, Container, Stack, TextField, Typography } from '@mui/material';

import useAllClipsQuery, { ClipsFormValues } from '../hooks/useAllClipsQuery';
import ClipsForm, { DEFAULT_VALUES } from './ClipsForm';
import ClipsTable from './ClipsTable';
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle';
import { AppPage } from '@/shared/components';
import PageHeader from '@/shared/components/PageHeader';

const ClipsPage = () => {
  const [formValues, setFormValues] = useState<ClipsFormValues>(DEFAULT_VALUES);
  const [textSearch, setTextSearch] = useState('');
  const [refreshInterval, setRefreshInterval] = useState(0);

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });

  const [sortModel, setSortModel] = useState([
    {
      field: 'createdAt',
      sort: 'desc',
    },
  ]);

  const clipsQuery = useAllClipsQuery(
    formValues,
    textSearch,
    paginationModel.pageSize,
    paginationModel.page,
    undefined,
    undefined,
    refreshInterval,
  );

  const total = clipsQuery.data?.rowCount || 0;
  const { isLoading } = clipsQuery;
  const title = 'Clips Management';

  const handleFilterAndSortChange = useCallback((values: ClipsFormValues) => {
    setFormValues(values);
  }, []);

  useDocumentTitle(title);

  const handleTextInputChange = useMemo(
    () =>
      debounce((event) => {
        setTextSearch(event.target.value);
      }, 500),
    [],
  );

  return (
    <AppPage title={title} requiredPermissions={['menu:clips']}>
      <Container maxWidth="xl" sx={{ marginTop: 4, marginBottom: 4 }}>
        <PageHeader title="Clips Management" />

        <ClipsForm onChange={handleFilterAndSortChange} />

        <Stack spacing={2}>
          {clipsQuery.isError ? (
            <Typography color="error" sx={{ textAlign: 'center' }}>
              An error occurred while fetching clips.
            </Typography>
          ) : null}

          {total !== undefined ? (
            <Typography variant="h6" sx={{ marginTop: 4 }}>
              {total} Records
            </Typography>
          ) : null}

          <Stack
            direction="row"
            spacing={2}
            sx={{ p: 3, justifyContent: 'space-between', alignItems: 'center' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-end', width: '100%', mb: 1 }}>
              <Search sx={{ color: 'action.active', mr: 1, my: 0.5 }} />
              <TextField
                id="input-with-sx"
                label="Search"
                onChange={handleTextInputChange}
                style={{ width: '300px' }}
              />
            </Box>
          </Stack>
          <ClipsTable
            setPaginationModel={setPaginationModel}
            paginationModel={paginationModel}
            setSortModel={setSortModel}
            sortModel={sortModel}
            isLoading={isLoading}
            error={clipsQuery.error}
            data={clipsQuery.data}
            refreshInterval={refreshInterval}
            setRefreshInterval={setRefreshInterval}
          />
        </Stack>
      </Container>
    </AppPage>
  );
};

export default ClipsPage;
